import { supabase } from '../lib/supabase';

// Phase 11 slot 14 (June → Juuni AI rebrand): storage keys renamed.
// Legacy `june_pending_signup` + `june_onboarding_required_<userId>` kept
// readable for one read each so users mid-flow at deploy time don't lose
// their state. Migration is one-way (read legacy, write canonical, remove
// legacy) — same pattern as AuthContext.
const PENDING_SIGNUP_KEY = 'juuni_pending_signup';
const LEGACY_PENDING_SIGNUP_KEY = 'june_pending_signup';
const DEFAULT_PLATFORM_ORG_ID = import.meta.env.VITE_DEFAULT_ORG_ID || '00000000-0000-0000-0000-000000000099';
const ONBOARDING_REQUIRED_PREFIX = 'juuni_onboarding_required_';
const LEGACY_ONBOARDING_REQUIRED_PREFIX = 'june_onboarding_required_';
const SETUP_COMPLETE_KEY = 'juuni_setup_complete';
const SETUP_COMPLETE_PREFIX = 'juuni_setup_complete_';

function migrateLegacyKey(legacyKey, canonicalKey) {
  try {
    if (localStorage.getItem(canonicalKey) !== null) return;
    const legacy = localStorage.getItem(legacyKey);
    if (legacy === null) return;
    localStorage.setItem(canonicalKey, legacy);
    localStorage.removeItem(legacyKey);
  } catch { /* storage blocked — non-critical */ }
}

function safeReadPendingSignup() {
  try {
    migrateLegacyKey(LEGACY_PENDING_SIGNUP_KEY, PENDING_SIGNUP_KEY);
    const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeWritePendingSignup(value) {
  try {
    if (!value) {
      localStorage.removeItem(PENDING_SIGNUP_KEY);
      return;
    }
    localStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify(value));
  } catch { /* intentionally empty */ }
}

function getSetupCompleteKey(orgId) {
  return `${SETUP_COMPLETE_PREFIX}${orgId}`;
}

function safeReadSetupComplete(orgId) {
  try {
    if (orgId && localStorage.getItem(getSetupCompleteKey(orgId)) === 'true') return true;
    return localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
  } catch {
    return false;
  }
}

function safeWriteSetupComplete(orgId, completed) {
  try {
    if (orgId) {
      if (completed) localStorage.setItem(getSetupCompleteKey(orgId), 'true');
      else localStorage.removeItem(getSetupCompleteKey(orgId));
    }
    if (completed) localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
    else localStorage.removeItem(SETUP_COMPLETE_KEY);
  } catch { /* intentionally empty */ }
}

function getOnboardingRequiredKey(userId) {
  return `${ONBOARDING_REQUIRED_PREFIX}${userId}`;
}

function safeReadOnboardingRequired(userId) {
  try {
    if (!userId) return false;
    // One-time legacy key migration on read (Phase 11 slot 14 rebrand).
    const canonical = `${ONBOARDING_REQUIRED_PREFIX}${userId}`;
    const legacy = `${LEGACY_ONBOARDING_REQUIRED_PREFIX}${userId}`;
    if (sessionStorage.getItem(canonical) === null) {
      const legacyVal = sessionStorage.getItem(legacy);
      if (legacyVal !== null) {
        sessionStorage.setItem(canonical, legacyVal);
        sessionStorage.removeItem(legacy);
      }
    }
    return sessionStorage.getItem(canonical) === 'true';
  } catch {
    return false;
  }
}

function safeWriteOnboardingRequired(userId, required) {
  try {
    if (!userId) return;
    if (required) sessionStorage.setItem(getOnboardingRequiredKey(userId), 'true');
    else sessionStorage.removeItem(getOnboardingRequiredKey(userId));
  } catch { /* intentionally empty */ }
}

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function getPendingSignupForUser(user) {
  const pending = safeReadPendingSignup();
  if (!pending) return null;
  if (!pending.email || pending.email === user?.email) return pending;
  return null;
}

function getDerivedName(user, pendingSignup) {
  return user?.user_metadata?.name || pendingSignup?.name || user?.email?.split('@')[0] || 'New User';
}

function getDerivedCompany(user, pendingSignup) {
  return user?.user_metadata?.company || pendingSignup?.company || '';
}

function buildInternalNotes(existingNotes, company) {
  if (existingNotes || !company) return existingNotes || undefined;
  return `Company: ${company}`;
}

export async function isSetupComplete(orgId) {
  if (!orgId) return false;

  const { data, error } = await supabase
    .from('organization')
    .select('setup_completed_at')
    .eq('id', orgId)
    .maybeSingle();

  if (error) {
    const missingSetupColumn = error.message?.includes('setup_completed_at');
    if (!missingSetupColumn) {
      console.warn('Failed to read organization setup state:', error.message);
      return safeReadSetupComplete(orgId);
    }

    const cachedState = safeReadSetupComplete(orgId);
    if (cachedState) return true;

    // Older live schemas may not have organization.setup_completed_at yet.
    // Infer completion from durable org activity rather than forcing admins
    // back into the setup wizard.
    const [jobsRes, configRes] = await Promise.all([
      supabase.from('job').select('id', { count: 'exact', head: true }).eq('org_id', orgId).limit(1),
      supabase.from('system_config').select('key', { count: 'exact', head: true }).eq('org_id', orgId).limit(1),
    ]);

    if (jobsRes.error || configRes.error) {
      console.warn('Failed to infer organization setup state:', jobsRes.error?.message || configRes.error?.message);
      return cachedState;
    }

    const inferredComplete = (jobsRes.count || 0) > 0 || (configRes.count || 0) > 0;
    if (inferredComplete) safeWriteSetupComplete(orgId, true);
    return inferredComplete;
  }

  const completed = Boolean(data?.setup_completed_at);
  safeWriteSetupComplete(orgId, completed);
  return completed;
}

export function cacheSetupComplete(orgId, completed) {
  safeWriteSetupComplete(orgId, completed);
}

async function fetchOwnProfile(userId) {
  const { data, error } = await supabase
    .from('individual')
    .select('id, org_id, name, email, role, primary_domain, timezone, internal_notes')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

async function ensureOrganization(user, role, pendingSignup, existingProfile) {
  const currentOrgId = user?.app_metadata?.org_id || existingProfile?.org_id || null;
  if (currentOrgId) return { orgId: currentOrgId, created: false };
  if (role === 'annotator') return { orgId: DEFAULT_PLATFORM_ORG_ID, created: false };

  const company = getDerivedCompany(user, pendingSignup);
  if (!company) return { orgId: null, created: false };

  const orgId = crypto.randomUUID();
  const slugBase = slugify(company) || 'organization';
  const slug = `${slugBase}-${orgId.slice(0, 8)}`;

  const { error } = await supabase.from('organization').insert({
    id: orgId,
    name: company,
    slug,
    plan_tier: 'starter',
  });

  if (error) throw error;
  return { orgId, created: true };
}

export function storePendingSignup(payload) {
  safeWritePendingSignup({
    name: payload?.name || '',
    company: payload?.company || '',
    role: payload?.role || 'annotator',
    email: payload?.email || '',
    createdAt: new Date().toISOString(),
  });
}

export function clearPendingSignup() {
  safeWritePendingSignup(null);
}

export function clearOnboardingRequired(userId) {
  safeWriteOnboardingRequired(userId, false);
}

export function getRoleForUser(user) {
  const pendingSignup = user ? getPendingSignupForUser(user) : null;
  // app_metadata is server-trusted (written only by sync_role_to_auth trigger);
  // user_metadata is client-editable and can't be trusted. Prefer app_metadata.
  // user_metadata remains a cold fallback only for brief mid-bootstrap windows
  // where the trigger hasn't fired yet. pendingSignup covers the signup-in-progress
  // flow before the profile is saved. Default to 'annotator' if nothing set.
  return user?.app_metadata?.role || user?.user_metadata?.role || pendingSignup?.role || 'annotator';
}

export function getDefaultAuthPath(role = 'annotator') {
  if (role === 'annotator') return '/onboarding';
  if (role === 'client') return '/client';
  if (role === 'god') return '/god';
  return '/setup';
}

export async function getPostAuthLandingPath(user) {
  if (!user?.id) return '/login';
  const pendingSignup = getPendingSignupForUser(user);
  const role = getRoleForUser(user);

  if (role === 'client') return '/client';
  const existingProfile = await fetchOwnProfile(user.id);
  const resolvedOrgId = user?.app_metadata?.org_id || existingProfile?.org_id || null;

  if (role !== 'annotator') {
    if (role === 'god') return '/god';
    if (!resolvedOrgId) return '/setup';
    return (await isSetupComplete(resolvedOrgId)) ? '/admin' : '/setup';
  }

  if (pendingSignup) return '/onboarding';
  if (safeReadOnboardingRequired(user.id)) return '/onboarding';

  return existingProfile ? '/' : '/onboarding';
}

export async function ensureAppIdentity(user) {
  if (!user?.id) {
    return { role: 'annotator', hasProfile: false, orgId: null, refreshed: false };
  }

  const pendingSignup = getPendingSignupForUser(user);
  const role = getRoleForUser(user);
  const existingProfile = await fetchOwnProfile(user.id);
  const { orgId, created: createdOrg } = await ensureOrganization(user, role, pendingSignup, existingProfile);
  const company = getDerivedCompany(user, pendingSignup);
  const now = new Date().toISOString();

  if (existingProfile) {
    const updates = { last_active_at: now };
    const derivedName = getDerivedName(user, pendingSignup);
    const internalNotes = buildInternalNotes(existingProfile.internal_notes, company);

    if (!existingProfile.name && derivedName) updates.name = derivedName;
    if (user.email && existingProfile.email !== user.email) updates.email = user.email;
    if (!existingProfile.role && role) updates.role = role;
    if (!existingProfile.org_id && orgId) updates.org_id = orgId;
    if (!existingProfile.primary_domain && role === 'annotator') updates.primary_domain = 'Audio';
    if (!existingProfile.internal_notes && internalNotes) updates.internal_notes = internalNotes;

    const { error: updateError } = await supabase.from('individual').update(updates).eq('id', user.id);
    if (updateError) throw updateError;
  } else {
    const { error: insertError } = await supabase.from('individual').insert({
      id: user.id,
      name: getDerivedName(user, pendingSignup),
      email: user.email,
      status: 'Active',
      tier: role === 'annotator' ? 'Specialist' : 'Generalist',
      primary_domain: 'Audio',
      career_level: 'Contributor',
      consent_status: 'Active',
      role,
      org_id: orgId,
      internal_notes: buildInternalNotes(undefined, company),
      created_at: now,
      last_active_at: now,
    });

    if (insertError) throw insertError;
  }

  if (!existingProfile && role === 'annotator') {
    safeWriteOnboardingRequired(user.id, true);
  }

  // Create referral record if signed up with a referral code
  if (!existingProfile && pendingSignup?.referrerId) {
    try {
      const { createReferral } = await import('./referralService');
      const result = await createReferral(pendingSignup.referrerId, user.id);
      if (result?.error) {
        console.warn('Referral creation skipped:', result.error);
      }
    } catch (refErr) {
      console.warn('Referral creation failed (non-blocking):', refErr.message);
    }
  }

  const needsRefresh = createdOrg || (!!orgId && user?.app_metadata?.org_id !== orgId) || (!user?.user_metadata?.role && !!role);
  if (needsRefresh) {
    await supabase.auth.refreshSession();
  }

  if (role !== 'annotator') {
    clearPendingSignup();
    clearOnboardingRequired(user.id);
  }

  return {
    role,
    hasProfile: true,
    orgId: orgId || existingProfile?.org_id || null,
    refreshed: needsRefresh,
  };
}
